import { Fragment, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Dialog, Transition } from '@headlessui/react';

import ChevronLeft from '@/icons/light/chevron-left.svg';
import Facebook from '@/icons/light/facebook.svg';
import Mail from '@/icons/light/mail.svg';
import Twitter from '@/icons/light/twitter.svg';
import { Button } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';

import type { FunctionComponent } from 'react';
import type { TypeModalGalleryProps } from './types';

const ModalShare = dynamic(() => import('@/molecules/modal-share'), {
  ssr: true,
});

const ModalForm = dynamic(() => import('@/molecules/modal-form'), {
  ssr: true,
});

const FormsTabs = dynamic(() => import('@/organisms/forms-tabs'), {
  ssr: true,
});

const ContactForm = dynamic(() => import('@/molecules/contact-form'), {
  ssr: true,
});

export const ModalGallery: FunctionComponent<TypeModalGalleryProps> = ({
  children,
  isOpen = true,
  closeModal,
  className = '',
  flat,
  isViewForms = true,
  unmount = false,
}: TypeModalGalleryProps) => {
  const [url, setUrl] = useState('');
  const [isOpenContactForm, setIsOpenContactForm] = useState(false);

  useEffect(() => {
    setUrl(document.URL);
    return () => setUrl('');
  }, []);

  return (
    <Transition appear show={isOpen} as={Fragment} unmount={unmount}>
      <Dialog
        as="div"
        className="relative z-20"
        onClose={closeModal}
        unmount={false}
      >
        <Transition.Child
          unmount={false}
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="bg-neutrals-900 bg-opacity-40 fixed inset-0" />
        </Transition.Child>

        <Transition.Child
          unmount={false}
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel
            className={`bg-neutrals-0 fixed inset-0 my-auto flex w-full transform flex-col overflow-hidden bg-white text-left shadow-xl transition-all lg:h-max lg:max-h-[calc(100vh-32px)] ${className}`}
          >
            <div className="bg-neutrals-0 sticky top-0 right-0 left-0 z-40 flex justify-between px-4 py-5 drop-shadow-[0_4px_12px_rgba(13,31,35,0.16)] lg:drop-shadow-none">
              <button
                type="button"
                onClick={closeModal}
                className="focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-hidden"
              >
                <ChevronLeft width={24} height={24} />
              </button>

              <ModalShare
                className="order-3 lg:order-2"
                title="Comparte esta vivienda"
                socialMedias={[
                  {
                    Icon: <Mail width={32} heigth={32} />,
                    text: 'Email',
                    href: 'mailto:?subject=Feedback&body=Message',
                  },
                  {
                    Icon: <Twitter width={32} heigth={32} />,
                    text: 'Twitter',
                    href: `https://twitter.com/intent/tweet?url=${url}`,
                  },
                  {
                    Icon: <Facebook width={32} heigth={32} />,
                    text: 'Facebook',
                    href: `https://www.facebook.com/sharer.php?u=${url}`,
                  },
                ]}
                gtm={{ itemId: flat?.codigoUnidadAlquiler }}
              />
            </div>

            <div className="relative mt-4 grow px-4 text-sm text-gray-500 lg:mt-0 lg:h-auto">
              {children}
            </div>

            {isViewForms && (
              <div className="bg-neutrals-0 sticky right-0 bottom-0 left-0 px-4 py-2 lg:hidden">
                <Button
                  size={ButtonSize.LARGE}
                  variant={ButtonVariant.PRIMARY}
                  className="w-full!"
                  onClick={() => setIsOpenContactForm(true)}
                >
                  Contactar
                </Button>

                <ModalForm
                  closeModal={() => setIsOpenContactForm(false)}
                  isOpen={isOpenContactForm}
                >
                  {flat ? <FormsTabs flat={flat} noTitle /> : <ContactForm />}
                </ModalForm>
              </div>
            )}
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

ModalGallery.displayName = 'ModalGallery';
